// The location's card processor account and the card follow-ups the cloud runs with it (server SV8a; ops
// payments-datacap.md §9; target model §3.4). The processor's API key is sealed in the cloud beside the account, set
// only by Radish staff and resellers (legacy LocationDataCapPatchRequest: admin or reseller), and shown by its last 4:
// no device, node, fact, sync item or stored result ever holds it (ADR-009, NM-040, D7).
//
// The register asks the cloud for what needs the Pay API: the tip capture of an auth-and-tip hold (Finalize Tips), a
// sale's void, a linked refund, raising or lowering a bar-tab hold and releasing one. Each names a stable
// `operationId`: an operation approved once is never sent again (a retry answers what was recorded), one whose answer
// was lost is looked up at the processor before anything is sent again, and one the processor refused may be asked
// again. Nothing is recorded as a fact here: the register records the outcome on the tender, as it does a terminal's.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { secret } from '../../define/secret.ts';
import { IsoInstant, LocationId, Money, TenderId } from '../../primitives/codecs.ts';
import { ConfigFieldsWritten } from '../config/index.ts';

/**
 * Legacy `locations.gateway` for in-store cards: Datacap (DC Direct on the LAN, the Pay API for follow-ups) or Clearent
 * (a cloud-connected terminal driven through Clearent's REST API with each terminal's own key, the device record's
 * Terminal ID; ops plan 2026-10-13-clearent). Legacy's Fluid-pay and Sandbox gateways are not carried.
 */
const Processor = z.enum(['datacap', 'clearent']).meta({ id: 'Processor' });

/**
 * Legacy `datacap_gateway_sandbox`: `sandbox` reaches Datacap's certification host, `production` its live one. A Clearent
 * account's `sandbox` reaches Clearent's integration host (gateway-int), `production` its live one.
 */
const ProcessorEnvironment = z.enum(['production', 'sandbox']).meta({ id: 'ProcessorEnvironment' });

export const ProcessorAccountView = z
  .object({
    locationId: LocationId,
    processor: Processor,
    environment: ProcessorEnvironment,
    /** Legacy `dc_direct_merchant_id`: not secret (the terminal sends it too). Unused by a Clearent account. */
    merchantRef: z.string(),
    /** The last 4 characters of the Pay API key, or null when none is kept. */
    secretLast4: z.string().max(4).nullable(),
    secretSetAt: IsoInstant.nullable(),
  })
  .meta({ id: 'ProcessorAccountView' });

/**
 * The config entity `payments.processor_account` (id = the location id; `PAYMENT_FIELDS`): what the node and every
 * register at the location hold of the account, offline too. A Datacap register sends `merchantRef` as the MerchantID of
 * every DC Direct request; the key is never here. A Clearent register needs no merchant ID (it may be empty): each terminal's
 * key is its device record's Terminal ID.
 */
export const ProcessorAccountFields = z
  .object({ locationId: LocationId, processor: Processor, environment: ProcessorEnvironment, merchantRef: z.string().max(64) })
  .meta({ id: 'ProcessorAccountFields' });

/**
 * What a register build reads of `payments.processor_account` (ops plan 2026-10-13-clearent), declared on every pull
 * beside its event versions (`maxSchemaVersions`, key `config:payments.processor_account`): 2 reads Clearent. An older
 * build reads only a Datacap account, and reads a missing or undecodable one as the Sandbox terminal, which approves
 * every card without charging it. So a build that does not declare 2 is never sent a Clearent account: it gets a Datacap
 * account whose merchant ID names no merchant (`NEEDS_UPDATE_MERCHANT_REF`), which runs the Datacap terminal, never the
 * Sandbox; with no DC Direct terminal at a Clearent location it takes no card.
 */
export const PROCESSOR_ACCOUNT_READER = 'config:payments.processor_account';
export const NEEDS_UPDATE_MERCHANT_REF = 'RADISH-UPDATE-REQUIRED';
/** The config readers this build declares, merged into its `maxSchemaVersions`. */
export const configReaderVersions: Readonly<Record<string, number>> = { [PROCESSOR_ACCOUNT_READER]: 2 };

/** A `payments.processor_account` device delta's fields as the puller's build can read them (see above). */
export function processorAccountForReader(
  fields: Readonly<Record<string, unknown>>, readable: Readonly<Record<string, number>>,
): Readonly<Record<string, unknown>> {
  if (fields['processor'] === undefined || fields['processor'] === 'datacap') return fields;
  if ((readable[PROCESSOR_ACCOUNT_READER] ?? 1) >= 2) return fields;
  return { ...fields, processor: 'datacap', merchantRef: NEEDS_UPDATE_MERCHANT_REF };
}

const PayApiKey = z.string().trim().min(8).max(200);
const MerchantRef = z.string().trim().min(1).max(64);
/** A Datacap account needs its merchant ID (the server refuses one without); a Clearent account may leave it empty. */
const AnyMerchantRef = z.string().trim().max(64);
/** A stable id per operation the register asks for (the same on every retry of it). */
const CardOperationId = z.string().regex(/^[0-9A-Za-z-]{8,64}$/);
/** The transaction the operation acts on: its PayAPI id (a capture's RefNo once captured). */
const ProcessorRef = z.string().min(1).max(64);

export const CardOperationInput = z
  .discriminatedUnion('kind', [
    /** Applied to the order, ex tip, and the tip (legacy capture). */
    z.object({ kind: z.literal('capture'), amount: Money, tip: Money }),
    z.object({ kind: z.literal('void_sale') }),
    /** Amount plus tip, as one figure (legacy `refundCentsAmount`). */
    z.object({ kind: z.literal('refund'), amount: Money }),
    /** The hold as first authorized, as it stands, and the amount it should hold now. */
    z.object({ kind: z.literal('adjust_hold'), originalAuthorized: Money, authorized: Money, amount: Money }),
    z.object({ kind: z.literal('void_hold') }),
  ])
  .meta({ id: 'CardOperationInput' });

export const CardOperationResult = z
  .discriminatedUnion('approved', [
    z.object({
      approved: z.literal(true),
      /** The processor's reference for what it did (a capture's RefNo is what a later refund or void names). */
      processorRef: z.string(),
      /** What the processor now holds or charged (amount plus tip after a capture), when it said. */
      authorized: Money.nullable(),
    }),
    z.object({ approved: z.literal(false), returnCode: z.string(), message: z.string() }),
  ])
  .meta({ id: 'CardOperationResult' });

// ---- errors ----------------------------------------------------------------------------------------------------------
export const ProcessorNotSet = defineError({
  code: 'payments.processor_not_set', httpStatus: 409, message: 'errors.payments.processor_not_set', retryable: false,
  details: z.object({ locationId: LocationId }),
});
/** Nothing was sent: the processor could not be reached, or would not say which transaction this is (ask again). */
export const ProcessorUnreachable = defineError({
  code: 'payments.processor_unreachable', httpStatus: 503, message: 'errors.payments.processor_unreachable', retryable: true,
  details: z.object({}),
});
/** It was sent and its answer was lost: it may have happened. Asking again looks it up first, never sends it blind. */
export const CardOperationUnknown = defineError({
  code: 'payments.card_operation_unknown', httpStatus: 503, message: 'errors.payments.card_operation_unknown', retryable: true,
  details: z.object({ operationId: z.string() }),
});
/**
 * It was sent, its answer was lost, and looking it up (waiting out Datacap's indexing lag) did not find it. Datacap
 * does a resent request again, so it is never sent again: a person decides. Asking again only looks it up again.
 */
export const CardOperationNeedsReview = defineError({
  code: 'payments.card_operation_needs_review', httpStatus: 409, message: 'errors.payments.card_operation_needs_review', retryable: false,
  details: z.object({ operationId: z.string() }),
});
/** The operation id was used for another operation, tender or figures. */
export const CardOperationConflict = defineError({
  code: 'payments.card_operation_conflict', httpStatus: 409, message: 'errors.payments.card_operation_conflict', retryable: false,
  details: z.object({ operationId: z.string() }),
});

/** Datacap would not issue a key for this merchant ID and public key (a token used already, a wrong merchant ID). */
export const ProcessorKeyRefused = defineError({
  code: 'payments.processor_key_refused', httpStatus: 422, message: 'errors.payments.processor_key_refused', retryable: false,
  details: z.object({ reason: z.string() }),
});

// ---- commands --------------------------------------------------------------------------------------------------------
export const SetProcessorAccount = defineCommand({
  name: 'payments.setProcessorAccount',
  summary: "Set the location's in-store processor account: Datacap (merchant ID, host and Pay API key) or Clearent (host only). Staff and resellers only; the key is never shown again.",
  input: z.object({
    locationId: LocationId,
    /** Left out: Datacap, as before Clearent was carried. */
    processor: Processor.default('datacap'),
    environment: ProcessorEnvironment,
    merchantRef: AnyMerchantRef,
    /** A new key (Datacap only: a Clearent account has none); null removes it; left out keeps the one kept. */
    secret: secret(PayApiKey).nullable().optional(),
  }),
  output: ProcessorAccountView,
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'The key is sealed with a cloud-held key and only the cloud calls the Pay API; it never reaches a node or register.',
  // The account without its key reaches the node and the registers as the config entity `payments.processor_account`.
  emits: [ConfigFieldsWritten],
  errors: [],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PATCH /v1/backoffice/locations/{location}/datacap'],
});

export const AcquireProcessorKey = defineCommand({
  name: 'payments.acquireProcessorKey',
  summary: "Generate the location's Pay API key from its merchant ID and the one-time Pay API public key Datacap issues, and save the account with the key sealed. Staff and resellers only; the key is never shown.",
  input: z.object({
    locationId: LocationId,
    environment: ProcessorEnvironment,
    merchantRef: MerchantRef,
    /** Datacap's one-time Pay API public key (legacy "Token"): it works once, so it is treated as a credential. */
    publicKey: secret(z.string().trim().min(1).max(200)),
  }),
  output: ProcessorAccountView,
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'Datacap is asked from the cloud, which seals the key it answers; the key never reaches a node or register.',
  // The account without its key reaches the node and the registers as the config entity `payments.processor_account`.
  emits: [ConfigFieldsWritten],
  errors: [ProcessorKeyRefused, ProcessorUnreachable],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/locations/{location}/data-cap/acquire-key'],
});

export const RunCardOperation = defineCommand({
  name: 'payments.runCardOperation',
  summary: 'Capture a tip, void a sale, refund, or raise, lower or release a hold through the Pay API (Datacap), at most once per operation id.',
  input: z.object({
    locationId: LocationId, operationId: CardOperationId, tenderId: TenderId, processorRef: ProcessorRef, operation: CardOperationInput,
    /**
     * The asking register's build version (its Android versionName, `0.1.0-qa.52`): the processor sees it as legacy's
     * register sent it, Pay API `User-Agent: RadishPOS/<version>` and Clearent "Radish POS version: <version>" (HW-023).
     * Left out by a register built before it: the cloud then names itself, as before.
     */
    appVersion: z.string().trim().min(1).max(32).regex(/^[0-9A-Za-z.+-]+$/).optional(),
    /**
     * B1707 (#2871 review B1): a `void_sale` that releases a card whose capture was declined. The cloud refuses it, without
     * asking the processor, once it holds a capture of the card approved or still on its way, so a register working from a
     * stale view never reverses a sale that captured since. Left out: an ordinary void, as before. Not part of the request
     * digest, so a retry from an older build stays the same operation.
     */
    release: z.literal(true).optional(),
    /**
     * B1951 (#2994 money review N1): a `void_hold` a register asks at sign-in for a hold it recorded voided with its
     * reversal requested. The cloud releases it only once it has the hold as voided (the void synced), so a register's
     * stale view never releases a tab another register charged; until then it answers `void_not_synced`, sending
     * nothing. Left out (a person's Void Authorization, or a register built before it): as before. A cloud built before
     * it drops it unread. Not part of the request digest.
     */
    requestedReversal: z.literal(true).optional(),
  }),
  output: CardOperationResult,
  // The register asks over the device's cloud link (legacy called the Pay API from the register with the key on it).
  permission: { session: 'device' },
  // Low, as giftCards.redeem: it is the register finishing its own card payment, and a dry run cannot ask a processor.
  risk: 'low',
  availability: 'cloud',
  origin: 'any',
  cloudReason: 'The Pay API needs the processor key, which only the cloud holds (ADR-009).',
  emits: [],
  errors: [ProcessorNotSet, ProcessorUnreachable, CardOperationUnknown, CardOperationNeedsReview, CardOperationConflict],
  dryRun: false,
  agent: { expose: false },
  legacy: ['PUT credit/preauth/{id}', 'POST credit/sale/{id}/void', 'POST credit/return/{RefNo}', 'PUT credit/preauth/incrementalauth/{id}', 'PUT credit/preauth/partialreversal/{id}'],
});

/**
 * What the processor holds for a card sale whose answer the register lost (payments-datacap.md §13): the record under
 * the attempt's own invoice, when it is the sale asked about (same kind and amount), or none. "None" is not proof the
 * card was not charged (a stored-offline sale reaches the processor later; a record can take a moment to be listed):
 * the register then keeps the attempt open for a manager.
 */
export const LostCardSale = z
  .discriminatedUnion('found', [
    z.object({
      found: z.literal(true),
      approved: z.boolean(),
      /** The sale's PayAPI id: the tender's reference for every follow-up. */
      payApiId: z.string(),
      authCode: z.string(),
      cardType: z.string(),
      lastFour: z.string().max(4),
      authorized: Money,
      /** The processor's words when it was not approved. */
      message: z.string(),
    }),
    z.object({ found: z.literal(false) }),
  ])
  .meta({ id: 'LostCardSale' });

export const FindLostCardSale = defineCommand({
  name: 'payments.findLostCardSale',
  summary: 'Look a card sale whose terminal answer was lost up at the processor (Datacap Pay API) by its own invoice number. Sends nothing to the card.',
  input: z.object({
    locationId: LocationId,
    /** The attempt's DC Direct InvoiceNo: day, the register's slot (0-8) and its count, 8 digits. */
    invoiceNo: z.string().regex(/^\d{3}[0-8]\d{4}$/),
    tranCode: z.enum(['EMVSale', 'EMVPreAuth']),
    amount: Money,
  }),
  output: LostCardSale,
  permission: { session: 'device' },
  risk: 'low',
  availability: 'cloud',
  origin: 'any',
  cloudReason: 'The Pay API needs the processor key, which only the cloud holds (ADR-009).',
  emits: [],
  errors: [ProcessorNotSet, ProcessorUnreachable],
  dryRun: false,
  agent: { expose: false },
  legacy: [],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
export const GetProcessorAccount = defineQuery({
  name: 'payments.processorAccount.get',
  summary: "The location's processor account (the key by its last 4 only), or null when none is set.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ account: ProcessorAccountView.nullable() }),
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: 'Only the cloud keeps the processor account.',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/backoffice/locations/{location}'],
});

export const processorCommands = [SetProcessorAccount, AcquireProcessorKey, RunCardOperation, FindLostCardSale] as const;
export const processorQueries = [GetProcessorAccount] as const;
